import { useSnackbar } from 'notistack'
import { useContext, useEffect, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'

import { fixAttributeDuplicatePaths } from 'features/ab-tests/api/utils/errors'
import { AbTestsAddEditContext } from 'features/ab-tests/contexts/AbTestsAddEditContext'
import { AbTestsAddSchema } from 'features/ab-tests/forms/AbTestsAddForm/abTestsAddSchema'
import { useFeatureFlag } from 'features/user/hooks/useFeatureFlag'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { MutationInputError } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { mapGraphQLErrors } from 'providers/tanstack-query/utils'
import { ROUTES } from 'routes'
import { toDTO } from './api/mapper'
import { CREATE_AB_TEST } from './api/mutation'
import { Data, Variables } from './api/types'
import { UseCreateAbTest } from './types'

export const useCreateAbTest = (): UseCreateAbTest => {
  const { workspaceId } = useWorkspaceParam()
  const navigate = useNavigate()
  const { t } = useTranslation('features/abTests', { keyPrefix: 'form.create' })
  const { enqueueSnackbar } = useSnackbar()
  const { setLoading } = useContext(AbTestsAddEditContext)
  const [errors, setErrors] = useState<FieldErrors<AbTestsAddSchema>>({})

  const { isFeatureEnabled: withYp2395 } = useFeatureFlag('yp2395AbTestsDivideTraffic')

  const { isPending: loading, mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: CREATE_AB_TEST,
    onCompleted: async (data: Data) => {
      const {
        createAbTest: { errors },
      } = data

      if (errors && errors.length > 0) {
        setErrors(
          mapGraphQLErrors(errors, (attribute: MutationInputError['attribute']) =>
            fixAttributeDuplicatePaths(attribute),
          ),
        )
        enqueueSnackbar(t('error'), { variant: 'error' })
      } else {
        setErrors({})
        navigate(generatePath(ROUTES.AB_TESTS.LIST, { workspaceId }))
        enqueueSnackbar(t('success'), { variant: 'success' })
      }
    },
  })

  useEffect(() => {
    setLoading(loading)
  }, [loading])

  const create: UseCreateAbTest['create'] = ({
    cookieLifetime,
    domainId,
    name,
    status,
    trafficSplitType,
    variants,
  }) =>
    void mutateAsync({
      input: toDTO({
        cookieLifetime,
        domainId,
        name,
        status,
        trafficSplitType,
        variants,
        withYp2395,
      }),
    })

  return { create, errors }
}
